import React, { useState, useEffect } from "react";
import {
  FaCheckCircle,
  FaBox,
  FaMoneyBillWave,
  FaShippingFast,
  FaTruck,
  FaHome,
  FaFileInvoice,
  FaWarehouse,
  FaClipboardCheck,
  FaUndo,
  FaStar
} from "react-icons/fa";
import OrderTimeline from "./OrderTimeline";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import base_url from "../../api";
import "./orderWorkflow.css";

const ORDER_STATUSES = {
  order_placed: { label: "Order Placed", icon: FaCheckCircle },
  payment_authorized: { label: "Payment Authorized", icon: FaMoneyBillWave },
  order_confirmed: { label: "Order Confirmed", icon: FaCheckCircle },
  stock_allocated: { label: "Stock Allocated", icon: FaBox },
  picking: { label: "Picking", icon: FaBox },
  packing: { label: "Packing", icon: FaBox },
  quality_check: { label: "Quality Check", icon: FaClipboardCheck },
  shipped: { label: "Shipped WITH Tracking", icon: FaShippingFast },
  in_transit: { label: "In Transit", icon: FaTruck },
  delivered: { label: "Delivered", icon: FaHome }
};

const PAYMENT_STATUSES = {
  payment_pending: "Payment Pending",
  payment_completed: "Payment Completed",
  payment_failed: "Payment Failed",
  payment_refunded: "Payment Refunded",
  chargeback_dispute_opened: "Chargeback/Dispute Opened"
};

const PAYMENT_METHODS = {
  e_transfer: "E-transfer",
  credit_card: "Credit Card",
  debit_card: "Debit Card",
  bank_transfer: "Bank Transfer",
  cash: "Cash",
  other: "Other"
};

const INVOICE_STATUSES = {
  invoice_generated: "Invoice Generated",
  invoice_sent: "Invoice Sent to Customer",
  invoice_paid: "Invoice Paid",
  invoice_cancelled: "Invoice Cancelled",
  invoice_amended: "Invoice Amended"
};

const SHIPMENT_STATUSES = {
  shipped: "Shipped",
  in_transit: "In Transit",
  out_for_delivery: "Out for Delivery",
  delivery_attempted: "Delivery Attempted",
  delivered: "Delivered",
  delivery_failed: "Delivery Failed / Returned to Sender"
};

const RETURN_STATUSES = {
  return_requested: "Return Requested",
  return_approved: "Return Approved",
  return_denied: "Return Denied",
  item_received_back: "Item Received Back",
  refund_issued: "Refund Issued",
  exchange_processed: "Exchange Processed"
};

const REFUND_STATUSES = {
  refund_pending: "Refund Pending",
  refund_completed: "Refund Completed"
};

const OrderWorkflowManager = ({ order, onUpdate, orderId }) => {
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Initialize with order prop or defaults
  const [orderStatus, setOrderStatus] = useState(order?.order_status || "order_placed");
  const [paymentStatus, setPaymentStatus] = useState(order?.payment_status || "payment_pending");
  const [paymentMethod, setPaymentMethod] = useState(order?.payment_method || "e_transfer");
  const [invoiceStatus, setInvoiceStatus] = useState(order?.invoice_status || "");
  const [transactionId, setTransactionId] = useState(order?.payment_transaction_id || "");
  
  // Warehouse Details
  const [pickingPerson, setPickingPerson] = useState(order?.picking_person || "");
  const [packingPerson, setPackingPerson] = useState(order?.packing_person || "");
  const [warehouseLocation, setWarehouseLocation] = useState(order?.warehouse_location || "");
  
  // Shipment Details
  const [courier, setCourier] = useState(order?.courier || "");
  const [tracking, setTracking] = useState(order?.tracking_number || "");
  const [shipmentStatus, setShipmentStatus] = useState(order?.shipment_status || "");
  const [estimatedDeliveryDate, setEstimatedDeliveryDate] = useState(order?.estimated_delivery_date || "");
  const [shippingCost, setShippingCost] = useState(order?.shipping_cost || "");
  const [signatureRequired, setSignatureRequired] = useState(order?.signature_required || false);
  
  // Delivery Confirmation
  const [deliveryTimestamp, setDeliveryTimestamp] = useState(order?.delivery_timestamp || "");
  const [proofOfDelivery, setProofOfDelivery] = useState(order?.proof_of_delivery || "");
  const [customerConfirmationEmailSent, setCustomerConfirmationEmailSent] = useState(order?.customer_confirmation_email_sent || false);
  
  // Post-Delivery Metrics
  const [customerSatisfaction, setCustomerSatisfaction] = useState(order?.customer_satisfaction || "");
  const [returnStatus, setReturnStatus] = useState(order?.return_status || "");
  const [refundStatus, setRefundStatus] = useState(order?.refund_status || "");
  const [complaintTicketId, setComplaintTicketId] = useState(order?.complaint_ticket_id || "");
  
  const [notes, setNotes] = useState(order?.status_notes || "");

  const steps = Object.keys(ORDER_STATUSES);

  // Fetch order details from database on mount or when orderId changes
  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId) {
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${base_url}/orders/all`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          console.error("Failed to fetch orders");
          setLoading(false);
          return;
        }

        const data = await response.json();
        
        // Find the order that matches our orderId
        const allOrders = data.orders || [];
        const foundOrder = allOrders.find(o => 
          (o.id && String(o.id) === String(orderId)) || 
          (o.orderId && String(o.orderId) === String(orderId)) ||
          (o.orderNo && String(o.orderNo) === String(orderId))
        );

        if (foundOrder) {
          console.log("Fetched order details:", foundOrder);
          
          // Update all state with fetched data (use nullish coalescing to handle empty strings)
          setOrderStatus(foundOrder.order_status || "order_placed");
          setPaymentStatus(foundOrder.payment_status || "payment_pending");
          setPaymentMethod(foundOrder.payment_method || "e_transfer");
          setInvoiceStatus(foundOrder.invoice_status || "");
          setTransactionId(foundOrder.payment_transaction_id || "");
          
          // Warehouse
          setPickingPerson(foundOrder.picking_person || "");
          setPackingPerson(foundOrder.packing_person || "");
          setWarehouseLocation(foundOrder.warehouse_location || "");
          
          // Shipment
          setCourier(foundOrder.courier || "");
          setTracking(foundOrder.tracking_number || "");
          setShipmentStatus(foundOrder.shipment_status || "");
          
          // Handle estimated_delivery_date - format for date input (YYYY-MM-DD)
          if (foundOrder.estimated_delivery_date) {
            const dateStr = foundOrder.estimated_delivery_date;
            // If it's already in YYYY-MM-DD format, use it directly
            if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
              // Extract just the date part (YYYY-MM-DD) from datetime strings
              const datePart = dateStr.split('T')[0].split(' ')[0];
              setEstimatedDeliveryDate(datePart);
            } else {
              // Try to parse as date
              const date = new Date(dateStr);
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                setEstimatedDeliveryDate(`${year}-${month}-${day}`);
              } else {
                setEstimatedDeliveryDate("");
              }
            }
          } else {
            setEstimatedDeliveryDate("");
          }
          
          setShippingCost(foundOrder.shipping_cost || "");
          setSignatureRequired(foundOrder.signature_required ? Boolean(foundOrder.signature_required) : false);
          
          // Delivery - handle datetime-local format
          if (foundOrder.delivery_timestamp) {
            const dateStr = foundOrder.delivery_timestamp;
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
              // Format for datetime-local input (YYYY-MM-DDTHH:mm)
              const formatted = date.toISOString().slice(0, 16);
              setDeliveryTimestamp(formatted);
            } else {
              setDeliveryTimestamp("");
            }
          } else {
            setDeliveryTimestamp("");
          }
          
          setProofOfDelivery(foundOrder.proof_of_delivery || "");
          setCustomerConfirmationEmailSent(foundOrder.customer_confirmation_email_sent ? Boolean(foundOrder.customer_confirmation_email_sent) : false);
          
          // Post-Delivery
          setCustomerSatisfaction(foundOrder.customer_satisfaction || "");
          setReturnStatus(foundOrder.return_status || "");
          setRefundStatus(foundOrder.refund_status || "");
          setComplaintTicketId(foundOrder.complaint_ticket_id || "");
          
          // Notes
          setNotes(foundOrder.status_notes || "");
        } else {
          console.warn(`Order ${orderId} not found in fetched orders`);
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId]); // Only depend on orderId, not onUpdate to avoid infinite loops

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      if (!orderId) {
        toast.error("Order ID is missing. Cannot update status.");
        return;
      }

      console.log("=== Starting order update for orderId:", orderId);
      console.log("Order Status:", orderStatus);
      console.log("Payment Status:", paymentStatus);
      console.log("Payment Method:", paymentMethod);
      console.log("Invoice Status:", invoiceStatus);

      let updateResults = {
        status: false,
        payment: false,
        invoice: false,
        shipment: false
      };

      // Update order status
      try {
        const statusResponse = await fetch(`${base_url}/orders/${orderId}/status`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            status: orderStatus,
            notes: notes || null,
            updatedBy: user.name || user.email || "Admin"
          })
        });

        const statusData = await statusResponse.json();
        console.log("Status update response:", statusData);

        if (statusResponse.ok && statusData.success) {
          updateResults.status = true;
        } else {
          console.error("Status update failed:", statusData);
          toast.error(statusData.error || `Failed to update status`);
        }
      } catch (err) {
        console.error("Status update error:", err);
        toast.error(`Status update error: ${err.message}`);
      }

      // Update payment status
      try {
        const paymentResponse = await fetch(`${base_url}/orders/${orderId}/payment`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            paymentStatus: paymentStatus,
            paymentMethod: paymentMethod || null,
            transactionId: transactionId || null,
            notes: notes || null
          })
        });

        const paymentData = await paymentResponse.json();
        console.log("Payment update response:", paymentData);

        if (paymentResponse.ok && paymentData.success) {
          updateResults.payment = true;
        } else {
          console.warn("Payment update failed:", paymentData);
        }
      } catch (err) {
        console.error("Payment update error:", err);
      }

      // Update invoice status if provided
      if (invoiceStatus) {
        try {
          const invoiceResponse = await fetch(`${base_url}/orders/${orderId}/invoice`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              invoiceStatus: invoiceStatus
            })
          });

          const invoiceData = await invoiceResponse.json();
          console.log("Invoice update response:", invoiceData);

          if (invoiceResponse.ok && invoiceData.success) {
            updateResults.invoice = true;
          } else {
            console.warn("Invoice update failed:", invoiceData);
          }
        } catch (err) {
          console.error("Invoice update error:", err);
        }
      }

      // Update shipment details (includes delivery confirmation and post-delivery metrics)
      // Always call shipment update to save warehouse, delivery, and post-delivery data
      try {
        const shipmentPayload = {
          courier: courier || null,
          trackingNumber: tracking || null,
          shipmentStatus: shipmentStatus || null,
          estimatedDeliveryDate: estimatedDeliveryDate || null,
          shippingCost: shippingCost || null,
          signatureRequired: signatureRequired || null,
          warehouseLocation: warehouseLocation || null,
          pickingPerson: pickingPerson || null,
          packingPerson: packingPerson || null,
          deliveryTimestamp: deliveryTimestamp || null,
          proofOfDelivery: proofOfDelivery || null,
          customerConfirmationEmailSent: customerConfirmationEmailSent || false,
          customerSatisfaction: customerSatisfaction || null,
          returnStatus: returnStatus || null,
          refundStatus: refundStatus || null,
          complaintTicketId: complaintTicketId || null
        };

        console.log("Shipment payload:", shipmentPayload);

        const shipmentResponse = await fetch(`${base_url}/orders/${orderId}/shipment`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(shipmentPayload)
        });

        const shipmentData = await shipmentResponse.json();
        console.log("Shipment update response:", shipmentData);

        if (shipmentResponse.ok && shipmentData.success) {
          updateResults.shipment = true;
        } else {
          console.error("Shipment update failed:", shipmentData);
        }
      } catch (err) {
        console.error("Shipment update error:", err);
      }

      // Check results
      const successfulUpdates = Object.values(updateResults).filter(r => r === true).length;
      const totalUpdates = Object.keys(updateResults).length;

      console.log("Update results:", updateResults);
      console.log(`Successfully updated ${successfulUpdates} out of ${totalUpdates} sections`);

      if (updateResults.status || updateResults.payment || updateResults.shipment) {
        toast.success(`Details saved successfully! (${successfulUpdates}/${totalUpdates} sections)`);
        setEditMode(false);
        // Refetch order details to update the UI with saved values
        const token = localStorage.getItem("token");
        const response = await fetch(`${base_url}/orders/all`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          const allOrders = data.orders || [];
          const foundOrder = allOrders.find(o => 
            (o.id && String(o.id) === String(orderId)) || 
            (o.orderId && String(o.orderId) === String(orderId)) ||
            (o.orderNo && String(o.orderNo) === String(orderId))
          );
          if (foundOrder) {
            // Update state with fresh data
            if (foundOrder.order_status) setOrderStatus(foundOrder.order_status);
            if (foundOrder.payment_status) setPaymentStatus(foundOrder.payment_status);
            if (foundOrder.payment_method) setPaymentMethod(foundOrder.payment_method);
            if (foundOrder.invoice_status) setInvoiceStatus(foundOrder.invoice_status || "");
            if (foundOrder.payment_transaction_id) setTransactionId(foundOrder.payment_transaction_id || "");
            if (foundOrder.picking_person) setPickingPerson(foundOrder.picking_person || "");
            if (foundOrder.packing_person) setPackingPerson(foundOrder.packing_person || "");
            if (foundOrder.warehouse_location) setWarehouseLocation(foundOrder.warehouse_location || "");
            if (foundOrder.courier) setCourier(foundOrder.courier || "");
            if (foundOrder.tracking_number) setTracking(foundOrder.tracking_number || "");
            if (foundOrder.shipment_status) setShipmentStatus(foundOrder.shipment_status || "");
            if (foundOrder.estimated_delivery_date) {
              const dateStr = foundOrder.estimated_delivery_date;
              if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
                setEstimatedDeliveryDate(dateStr.split('T')[0].split(' ')[0]);
              } else {
                const date = new Date(dateStr);
                if (!isNaN(date.getTime())) {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  setEstimatedDeliveryDate(`${year}-${month}-${day}`);
                }
              }
            }
            if (foundOrder.shipping_cost) setShippingCost(foundOrder.shipping_cost || "");
            if (foundOrder.signature_required !== undefined) setSignatureRequired(Boolean(foundOrder.signature_required));
            if (foundOrder.delivery_timestamp) {
              const date = new Date(foundOrder.delivery_timestamp);
              if (!isNaN(date.getTime())) {
                setDeliveryTimestamp(date.toISOString().slice(0, 16));
              }
            }
            if (foundOrder.proof_of_delivery) setProofOfDelivery(foundOrder.proof_of_delivery || "");
            if (foundOrder.customer_confirmation_email_sent !== undefined) setCustomerConfirmationEmailSent(Boolean(foundOrder.customer_confirmation_email_sent));
            if (foundOrder.customer_satisfaction) setCustomerSatisfaction(foundOrder.customer_satisfaction || "");
            if (foundOrder.return_status) setReturnStatus(foundOrder.return_status || "");
            if (foundOrder.refund_status) setRefundStatus(foundOrder.refund_status || "");
            if (foundOrder.complaint_ticket_id) setComplaintTicketId(foundOrder.complaint_ticket_id || "");
            if (foundOrder.status_notes) setNotes(foundOrder.status_notes || "");
          }
        }
        if (onUpdate) onUpdate();
      } else {
        toast.error("Failed to save details. Please check console for errors.");
      }
    } catch (err) {
      console.error("Error updating status:", err);
      toast.error(`Error: ${err.message || "Something went wrong. Please try again."}`);
    }
  };

  if (loading) {
    return (
      <div className="owm-wrapper">
        <div style={{ padding: "20px", textAlign: "center" }}>Loading order details...</div>
      </div>
    );
  }

  return (
    <div className="owm-wrapper">
      <ToastContainer />

      {/* TIMELINE */}
      {/* <section className="owm-zone">
        <h3 className="owm-zone-title">Order Progress</h3>
        <OrderTimeline
          steps={steps}
          current={orderStatus}
          config={ORDER_STATUSES}
        />
      </section> */}

      {/* OVERVIEW */}
      <section className="owm-zone">
        <div className="owm-overview-grid">
          <div className="owm-overview-card primary">
            <span className="label">Order Status</span>
            <strong>{ORDER_STATUSES[orderStatus]?.label}</strong>
          </div>

          <div className="owm-overview-card">
            <span className="label">Payment Status</span>
            <strong>{PAYMENT_STATUSES[paymentStatus] || "N/A"}</strong>
          </div>

          {paymentMethod && (
            <div className="owm-overview-card">
              <span className="label">Payment Method</span>
              <strong>{PAYMENT_METHODS[paymentMethod] || paymentMethod}</strong>
            </div>
          )}

          {invoiceStatus && (
            <div className="owm-overview-card">
              <span className="label">Invoice Status</span>
              <strong>{INVOICE_STATUSES[invoiceStatus] || invoiceStatus}</strong>
            </div>
          )}

          {tracking && (
            <div className="owm-overview-card">
              <span className="label">Tracking</span>
              <strong>{tracking}</strong>
            </div>
          )}

          {shipmentStatus && (
            <div className="owm-overview-card">
              <span className="label">Shipment Status</span>
              <strong>{SHIPMENT_STATUSES[shipmentStatus] || shipmentStatus}</strong>
            </div>
          )}

          {returnStatus && (
            <div className="owm-overview-card">
              <span className="label">Return Status</span>
              <strong>{RETURN_STATUSES[returnStatus] || returnStatus}</strong>
            </div>
          )}
        </div>

        {!editMode && (
          <button className="owm-btn primary" onClick={() => setEditMode(true)}>
            Edit Status
          </button>
        )}
      </section>

      {/* EDIT MODE */}
      {editMode && (
        <section className="owm-zone">
          <div className="owm-edit-group">
            <h4>Order Status</h4>
            <select value={orderStatus} onChange={e => setOrderStatus(e.target.value)}>
              {steps.map(s => (
                <option key={s} value={s}>
                  {ORDER_STATUSES[s].label}
                </option>
              ))}
            </select>
          </div>

          <div className="owm-edit-group">
            <h4>💳 Payment Tracking</h4>
            <div className="owm-form-grid">
              <div>
                <label className="owm-field-label">Payment Status</label>
                <select value={paymentStatus} onChange={e => setPaymentStatus(e.target.value)}>
                  {Object.entries(PAYMENT_STATUSES).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="owm-field-label">Payment Method</label>
                <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                  {Object.entries(PAYMENT_METHODS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="owm-field-label">Transaction ID</label>
                <input
                  placeholder="Transaction ID (optional)"
                  value={transactionId}
                  onChange={e => setTransactionId(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="owm-edit-group">
            <h4>📄 Invoice Status</h4>
            <select value={invoiceStatus} onChange={e => setInvoiceStatus(e.target.value)}>
              <option value="">Select Invoice Status</option>
              {Object.entries(INVOICE_STATUSES).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          <div className="owm-edit-group">
            <h4><FaWarehouse /> Warehouse Details</h4>
            <div className="owm-form-grid">
              <div>
                <label className="owm-field-label">Picking Person/Team</label>
                <input
                  placeholder="Picking person or team name"
                  value={pickingPerson}
                  onChange={e => setPickingPerson(e.target.value)}
                />
              </div>
              <div>
                <label className="owm-field-label">Packing Person/Team</label>
                <input
                  placeholder="Packing person or team name"
                  value={packingPerson}
                  onChange={e => setPackingPerson(e.target.value)}
                />
              </div>
              <div>
                <label className="owm-field-label">Warehouse Location</label>
                <input
                  placeholder="Warehouse location (if multiple)"
                  value={warehouseLocation}
                  onChange={e => setWarehouseLocation(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="owm-edit-group">
            <h4><FaShippingFast /> Shipment & Logistics</h4>
            <div className="owm-form-grid">
              <div>
                <label className="owm-field-label">Courier</label>
                <input
                  placeholder="FedEx, UPS, DHL, etc."
                  value={courier}
                  onChange={e => setCourier(e.target.value)}
                />
              </div>
              <div>
                <label className="owm-field-label">Tracking Number</label>
                <input
                  placeholder="Tracking number"
                  value={tracking}
                  onChange={e => setTracking(e.target.value)}
                />
              </div>
              <div>
                <label className="owm-field-label">Shipment Status</label>
                <select value={shipmentStatus} onChange={e => setShipmentStatus(e.target.value)}>
                  <option value="">Select Status</option>
                  {Object.entries(SHIPMENT_STATUSES).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="owm-field-label">Estimated Delivery Date</label>
                <input
                  type="date"
                  value={estimatedDeliveryDate}
                  onChange={e => setEstimatedDeliveryDate(e.target.value)}
                />
              </div>
              <div>
                <label className="owm-field-label">Shipping Cost</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={shippingCost}
                  onChange={e => setShippingCost(e.target.value)}
                />
              </div>
              <div>
                <label className="owm-field-label" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="checkbox"
                    checked={signatureRequired}
                    onChange={e => setSignatureRequired(e.target.checked)}
                  />
                  Signature Required?
                </label>
              </div>
            </div>
          </div>

          <div className="owm-edit-group">
            <h4><FaCheckCircle /> Delivery Confirmation</h4>
            <div className="owm-form-grid">
              <div>
                <label className="owm-field-label">Delivery Timestamp</label>
                <input
                  type="datetime-local"
                  value={deliveryTimestamp}
                  onChange={e => setDeliveryTimestamp(e.target.value)}
                />
              </div>
              <div>
                <label className="owm-field-label">Proof of Delivery</label>
                <input
                  placeholder="Proof of delivery reference"
                  value={proofOfDelivery}
                  onChange={e => setProofOfDelivery(e.target.value)}
                />
              </div>
              <div>
                <label className="owm-field-label" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="checkbox"
                    checked={customerConfirmationEmailSent}
                    onChange={e => setCustomerConfirmationEmailSent(e.target.checked)}
                  />
                  Customer Confirmation Email Sent
                </label>
              </div>
            </div>
          </div>

          <div className="owm-edit-group">
            <h4><FaStar /> Post-Delivery Metrics</h4>
            <div className="owm-form-grid">
              <div>
                <label className="owm-field-label">Customer Satisfaction / Feedback</label>
                <textarea
                  placeholder="Customer feedback or satisfaction rating"
                  value={customerSatisfaction}
                  onChange={e => setCustomerSatisfaction(e.target.value)}
                  rows="3"
                />
              </div>
              <div>
                <label className="owm-field-label">Return Status</label>
                <select value={returnStatus} onChange={e => setReturnStatus(e.target.value)}>
                  <option value="">Select Return Status</option>
                  {Object.entries(RETURN_STATUSES).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="owm-field-label">Refund Status</label>
                <select value={refundStatus} onChange={e => setRefundStatus(e.target.value)}>
                  <option value="">Select Refund Status</option>
                  {Object.entries(REFUND_STATUSES).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="owm-field-label">Complaint / Support Ticket ID</label>
                <input
                  placeholder="Ticket ID (if applicable)"
                  value={complaintTicketId}
                  onChange={e => setComplaintTicketId(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="owm-edit-group">
            <h4>📝 Notes</h4>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add notes about this order..."
              rows="4"
            />
          </div>

          <div className="owm-actions">
            <button className="owm-btn ghost" onClick={() => setEditMode(false)}>
              Cancel
            </button>
            <button className="owm-btn primary" onClick={handleSave}>
              Save Changes
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default OrderWorkflowManager;
